import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button/Button';
import Divider from '@mui/material/Divider/Divider';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';

import WaveUIAccordion from '../WaveUIAccordion';
import NewNoteForm from './components/NewNoteForm';
import NoteItem from './components/NoteItem';
import ReplyInput from './components/ReplyInput';
import SearchBox from './components/SearchBox';
import useAgentNotes from './hooks/useAgentNotes';

type AgentNotesProps = {
  title: string;
  agentNotesState: ReturnType<typeof useAgentNotes>;
  elevation?: number;
};

const AgentNotes: FC<AgentNotesProps> = ({
  title,
  agentNotesState,
  elevation = 2,
}) => {
  const { t } = useTranslation(['notes', 'common']);

  const [isNewNoteFormOpen, setIsNewNoteFormOpen] = useState(false);

  const {
    options,
    agentNotes,
    handleApplyFilters,
    handleCreateAgentNote,
    handleReply,
  } = agentNotesState;

  return (
    <WaveUIAccordion title={title} defaultExpanded elevation={elevation}>
      <Stack spacing={2}>
        <SearchBox
          options={options}
          onApplyFilters={(filters) => {
            void handleApplyFilters(filters);
          }}
        />

        <Divider />

        <Stack
          spacing={3}
          sx={{
            maxHeight: '50vh',
            overflowY: 'auto',
            mx: -2,
            px: 2,
          }}
        >
          <>
            {isNewNoteFormOpen ? (
              <NewNoteForm
                onCreateAgentNote={(note) => {
                  void handleCreateAgentNote(note);
                }}
                onDiscardCreateNote={() => {
                  setIsNewNoteFormOpen(false);
                }}
              />
            ) : (
              agentNotes &&
              agentNotes.length === 0 && (
                <Typography variant='body2' color='text.secondary'>
                  {t('notes:noNotesFound')}
                </Typography>
              )
            )}

            {agentNotes &&
              agentNotes.map((noteGroup, groupIndex) => (
                <div key={groupIndex}>
                  <Stack spacing={2}>
                    {noteGroup.map((note, noteIndex) => (
                      <NoteItem
                        key={note.note_id}
                        isReplyItem={noteIndex !== 0}
                        isReplyInputVisible={
                          noteIndex === 0 && noteGroup.length === 1
                        }
                        note={note}
                        onReply={(note, conversationID) => {
                          void handleReply(note, conversationID);
                        }}
                      />
                    ))}
                  </Stack>

                  {noteGroup.length > 1 && (
                    <Stack sx={{ pl: 8, mt: 2 }}>
                      <ReplyInput
                        conversationID={
                          noteGroup[groupIndex]?.conversation_id ||
                          noteGroup[groupIndex]?.note_id
                        }
                        onReply={(note, conversationID) => {
                          void handleReply(note, conversationID);
                        }}
                      />
                    </Stack>
                  )}
                </div>
              ))}
          </>
        </Stack>

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button variant='outlined'>{t('common:viewAll')}</Button>
          <Button
            variant='contained'
            onClick={() => setIsNewNoteFormOpen(true)}
          >
            {t('notes:addNote')}
          </Button>
        </Stack>
      </Stack>
    </WaveUIAccordion>
  );
};

export default AgentNotes;
